/**
 * 📋 Serviço de detalhamento de instância — Etapas, Eventos e Auditoria
 * 
 * Fornece dados para as 3 novas abas da tela de Execução:
 * - Etapas: stages com status, responsável, SLA
 * - Timeline (Eventos): workflow_instance_events_v9
 * - Auditoria: rastreabilidade de blueprint/versão/snapshot
 */
import { supabase } from '../../../lib/supabaseClient';
import type { StageDetail, InstanceEvent, ExecutionAuditInfo } from '../types';

function diffMinutes(start?: string | null, end?: string | null): number | null {
  if (!start || !end) return null;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(diff) || diff < 0) return null;
  return diff / (1000 * 60);
}

async function fetchTemplateStages(templateId: string, organizationId: string) {
  const stageQueries = [
    () =>
      supabase
        .from('workflow_stages_v9')
        .select('id, name, stage_order, sla_hours, wip_limit, warning_threshold_percent, require_approval, default_assignee_role')
        .eq('template_id', templateId)
        .eq('organization_id', organizationId)
        .order('stage_order', { ascending: true }),
    () =>
      supabase
        .from('workflow_stages_v9')
        .select('id, name, stage_order, sla_hours, wip_limit, warning_threshold_percent, require_approval, default_assignee_role')
        .eq('template_id', templateId)
        .order('stage_order', { ascending: true }),
    () =>
      supabase
        .from('workflow_stages_v9')
        .select('id, name, stage_order, sla_hours, default_assignee_role')
        .eq('template_id', templateId)
        .order('stage_order', { ascending: true }),
  ];

  for (const runQuery of stageQueries) {
    const result = await runQuery();
    if (!result.error) {
      return result.data ?? [];
    }
  }

  return [];
}

// ============================================================================
// ETAPAS — stages da instância com dados da task correspondente
// ============================================================================

export async function getInstanceStages(
  organizationId: string,
  instanceId: string,
): Promise<StageDetail[]> {
  if (!organizationId || !instanceId) return [];

  // 1) Buscar a instância para pegar template_id
  const { data: instance, error: instError } = await supabase
    .from('workflow_instances_v9')
    .select('template_id, started_at')
    .eq('id', instanceId)
    .eq('organization_id', organizationId)
    .single();

  if (instError || !instance) return [];

  // 2) Buscar todas as stages do template
  const stages = await fetchTemplateStages(instance.template_id, organizationId);
  if (!stages || stages.length === 0) return [];

  // 3) Buscar todas as tasks desta instância
  const { data: tasks, error: tasksError } = await supabase
    .from('workflow_tasks_v9')
    .select('id, stage_id, status, assigned_to, assigned_at, started_at, completed_at, sla_deadline, is_overdue, duration_minutes, created_at, metadata')
    .eq('instance_id', instanceId)
    .eq('organization_id', organizationId);

  if (tasksError) return [];

  const { data: stageEvents } = await supabase
    .from('workflow_instance_events_v9')
    .select('stage_id, event_type, created_at')
    .eq('instance_id', instanceId)
    .eq('organization_id', organizationId)
    .in('event_type', ['stage_advanced', 'task_started']);

  // 4) Contar WIP por stage
  const wipCounts = new Map<string, number>();
  if (tasks) {
    tasks.forEach((t: any) => {
      if (['pending', 'in_progress'].includes(t.status)) {
        wipCounts.set(t.stage_id, (wipCounts.get(t.stage_id) ?? 0) + 1);
      }
    });
  }

  const stageEntryMap = new Map<string, string>();
  (stageEvents ?? []).forEach((event: any) => {
    if (!event.stage_id || !event.created_at) return;
    const current = stageEntryMap.get(event.stage_id);
    if (!current || new Date(event.created_at).getTime() < new Date(current).getTime()) {
      stageEntryMap.set(event.stage_id, event.created_at);
    }
  });

  // 5) Montar resultado cruzando stages com tasks
  let previousCompletedAt: string | undefined = instance.started_at ?? undefined;

  return stages.map((stage: any) => {
    const task = tasks?.find((t: any) => t.stage_id === stage.id);
    let status: StageDetail['status'] = 'pending';
    if (task) {
      if (task.status === 'completed') status = 'completed';
      else if (task.status === 'skipped') status = 'skipped';
      else if (task.status === 'in_progress') status = 'in_progress';
      else status = 'pending';
    }

    const enteredAt =
      stageEntryMap.get(stage.id) ??
      previousCompletedAt ??
      task?.started_at ??
      task?.created_at ??
      instance.started_at ??
      undefined;
    const processingMinutes: number | null =
      diffMinutes(task?.started_at, task?.completed_at) ??
      (typeof task?.duration_minutes === 'number' ? task.duration_minutes : null);
    const leadTimeMinutes = diffMinutes(enteredAt, task?.completed_at);
    const durationMinutes = processingMinutes;
    const slaLimitMinutes = stage.sla_hours ? stage.sla_hours * 60 : null;

    // Compute slaStatus
    let slaStatus: StageDetail['slaStatus'] = 'no_sla';
    if (slaLimitMinutes) {
      if (status === 'completed' || status === 'skipped') {
        slaStatus = leadTimeMinutes !== null && leadTimeMinutes <= slaLimitMinutes
          ? 'completed_on_time'
          : 'completed_late';
      } else if (task?.is_overdue) {
        slaStatus = 'overdue';
      } else if (task?.sla_deadline) {
        const remaining = new Date(task.sla_deadline).getTime() - Date.now();
        const totalMs = slaLimitMinutes * 60 * 1000;
        slaStatus = remaining < totalMs * 0.2 ? 'warning' : 'on_track';
      } else {
        slaStatus = 'on_track';
      }
    }

    if (task?.completed_at) {
      previousCompletedAt = task.completed_at;
    }

    return {
      id: stage.id,
      name: stage.name,
      stageOrder: stage.stage_order,
      status,
      assignedRoleId: task?.metadata?.roleId ?? undefined,
      assignedRoleName: task?.metadata?.roleName ?? task?.metadata?.role ?? stage.default_assignee_role ?? undefined,
      assignedTo: task?.assigned_to ?? undefined,
      assignedToName: undefined,
      assignedUserId: task?.assigned_to ?? undefined,
      assignedUserName: undefined,
      claimedAt: task?.assigned_at ?? undefined,
      slaHours: stage.sla_hours ?? undefined,
      slaDeadline: task?.sla_deadline ?? undefined,
      startedAt: task?.started_at ?? undefined,
      completedAt: task?.completed_at ?? undefined,
      enteredAt,
      durationMinutes,
      leadTimeMinutes,
      processingMinutes,
      slaStatus,
      taskId: task?.id ?? undefined,
      isOverdue: task?.is_overdue ?? false,
      wipLimit: stage.wip_limit ?? undefined,
      currentWip: wipCounts.get(stage.id) ?? 0,
      warningThresholdPercent: stage.warning_threshold_percent ?? undefined,
      requireApproval: stage.require_approval ?? undefined,
    };
  });
}

// ============================================================================
// EVENTOS — timeline de eventos da instância
// ============================================================================

export async function getInstanceEvents(
  organizationId: string,
  instanceId: string,
  limit = 50,
  beforeCursor?: string, // ISO timestamp — load events before this point
): Promise<{ events: InstanceEvent[]; hasMore: boolean; nextCursor?: string }> {
  if (!organizationId || !instanceId) return { events: [], hasMore: false };

  let query = supabase
    .from('workflow_instance_events_v9')
    .select(`
      id,
      instance_id,
      event_type,
      description,
      stage_id,
      task_id,
      performed_by,
      performed_by_name,
      performed_by_role,
      metadata,
      old_value,
      new_value,
      created_at,
      stage:workflow_stages_v9(name)
    `)
    .eq('organization_id', organizationId)
    .eq('instance_id', instanceId)
    .order('created_at', { ascending: false })
    .limit(limit + 1); // fetch one extra to detect hasMore

  if (beforeCursor) {
    query = query.lt('created_at', beforeCursor);
  }

  const { data, error } = await query;

  if (error) {
    console.warn('[instanceDetailService] Falha ao buscar eventos:', error.message);
    return { events: [], hasMore: false };
  }

  const rows = data ?? [];
  const hasMore = rows.length > limit;
  const trimmed = hasMore ? rows.slice(0, limit) : rows;
  const nextCursor = hasMore ? trimmed[trimmed.length - 1].created_at : undefined;

  const events: InstanceEvent[] = trimmed.map((row: any) => ({
    id: row.id,
    instanceId: row.instance_id,
    eventType: row.event_type,
    description: row.description ?? undefined,
    stageId: row.stage_id ?? undefined,
    stageName: row.stage?.name ?? undefined,
    taskId: row.task_id ?? undefined,
    performedBy: row.performed_by ?? undefined,
    performedByName: row.performed_by_name ?? undefined,
    performedByRole: row.performed_by_role ?? undefined,
    metadata: row.metadata ?? undefined,
    oldValue: row.old_value ?? undefined,
    newValue: row.new_value ?? undefined,
    createdAt: row.created_at,
  }));

  return { events, hasMore, nextCursor };
}

/**
 * Registrar evento manualmente (para ações do app, não cobertas por triggers)
 */
export async function logInstanceEvent(params: {
  organizationId: string;
  instanceId: string;
  eventType: InstanceEvent['eventType'];
  description?: string;
  stageId?: string;
  taskId?: string;
  performedBy?: string;
  performedByName?: string;
  performedByRole?: string;
  metadata?: Record<string, any>;
  oldValue?: Record<string, any>;
  newValue?: Record<string, any>;
}): Promise<void> {
  const { error } = await supabase
    .from('workflow_instance_events_v9')
    .insert({
      organization_id: params.organizationId,
      instance_id: params.instanceId,
      event_type: params.eventType,
      description: params.description,
      stage_id: params.stageId,
      task_id: params.taskId,
      performed_by: params.performedBy,
      performed_by_name: params.performedByName,
      performed_by_role: params.performedByRole,
      metadata: params.metadata ?? {},
      old_value: params.oldValue,
      new_value: params.newValue,
    });

  if (error) {
    console.warn('[instanceDetailService] Falha ao registrar evento:', error.message);
  }
}

// ============================================================================
// AUDITORIA — dados de rastreabilidade blueprint → instância
// ============================================================================

export async function getInstanceAuditInfo(
  organizationId: string,
  instanceId: string,
): Promise<ExecutionAuditInfo | null> {
  if (!organizationId || !instanceId) return null;

  const { data: instance, error } = await supabase
    .from('workflow_instances_v9')
    .select(`
      id,
      template_id,
      blueprint_id,
      blueprint_version_id,
      initiated_by,
      created_at,
      metadata,
      template:workflow_templates_v9(id, name, version)
    `)
    .eq('id', instanceId)
    .eq('organization_id', organizationId)
    .single();

  if (error || !instance) return null;

  // Se tem blueprint_id, buscar dados da versão
  let blueprintName: string | undefined;
  let versionSemver: string | undefined;
  let snapshotHash: string | undefined;

  if (instance.blueprint_id) {
    const { data: bp } = await supabase
      .from('process_blueprints')
      .select('name')
      .eq('id', instance.blueprint_id)
      .maybeSingle();
    blueprintName = bp?.name ?? undefined;
  }

  if (instance.blueprint_version_id) {
    const { data: ver } = await supabase
      .from('process_blueprint_versions')
      .select('version_semver, snapshot_hash')
      .eq('id', instance.blueprint_version_id)
      .maybeSingle();
    versionSemver = ver?.version_semver ?? undefined;
    snapshotHash = ver?.snapshot_hash ?? undefined;
  }

  // Verificar se existe versão mais recente publicada para este blueprint
  let isOutdated = false;
  let latestVersionSemver: string | undefined;
  let latestVersionId: string | undefined;

  if (instance.blueprint_id && instance.blueprint_version_id) {
    const { data: latestVersions } = await supabase
      .from('process_blueprint_versions')
      .select('id, version_semver, created_at')
      .eq('blueprint_id', instance.blueprint_id)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (latestVersions && latestVersions.id !== instance.blueprint_version_id) {
      isOutdated = true;
      latestVersionSemver = latestVersions.version_semver;
      latestVersionId = latestVersions.id;
    }
  }

  // Fallback: checar metadata
  const meta = (instance.metadata ?? {}) as Record<string, any>;

  return {
    blueprintId: instance.blueprint_id ?? meta.blueprint_id ?? undefined,
    blueprintName,
    blueprintVersionId: instance.blueprint_version_id ?? meta.blueprint_version_id ?? undefined,
    versionSemver: versionSemver ?? meta.version_semver ?? undefined,
    snapshotHash: snapshotHash ?? meta.snapshot_hash ?? undefined,
    isOutdated,
    latestVersionSemver,
    latestVersionId,
    templateId: instance.template_id,
    templateName: (instance as any).template?.name ?? undefined,
    templateVersion: (instance as any).template?.version ?? undefined,
    createdAt: instance.created_at,
    initiatedBy: instance.initiated_by ?? undefined,
  };
}
